import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Card,
  List,
  Switch,
  Input,
  Row,
  Col,
  Button
} from "@lockerpm/design";

import {
  SmileOutlined,
  SettingOutlined,
  DeleteOutlined
} from "@ant-design/icons";

import commonComponents from "../../../components/common";

import global from "../../../config/global";
import scamSettingServices from "../../../services/scam-setting";
import scamReportServices from "../../../services/scam-report";

const ScamSetting = () => {
  const { t } = useTranslation();
  const { PageHeader } = commonComponents;

  const [reportsIsAnonymous, setReportsIsAnonymous] = useState(false)
  const [settingOptions, setSettingOptions] = useState([])
  const [url, setUrl] = useState('');
  const [callingAPIUrl, setCallingAPIUrl] = useState(false);
  const [whitelistUrls, setWhitelistUrls] = useState([]);

  const incognitoModeOptions = useMemo(() => {
    return [
      {
        enabled: reportsIsAnonymous,
        label: 'toggle'
      }
    ]
  }, [reportsIsAnonymous])

  useEffect(() => {
    fetchReportsIsAnonymous();
    fetchScamSettings();
    fetchWhitelistUrls();
  }, [])

  const fetchReportsIsAnonymous = async () => {
    await scamReportServices.get_reports_is_anonymous().then((res) => {
      setReportsIsAnonymous(res.is_anonymous || false)
    }).catch((error) => {
      setReportsIsAnonymous(false)
      global.pushError(error)
    })
  }

  const fetchScamSettings = async () => {
    await scamSettingServices.get_scam_settings().then((res) => {
      setSettingOptions(res)
    }).catch((error) => {
      global.pushError(error)
    })
  }

  const fetchWhitelistUrls = async () => {
    await scamSettingServices.get_whitelist_urls().then((res) => {
      setWhitelistUrls(res.results || [])
    }).catch((error) => {
      setWhitelistUrls([])
      global.pushError(error)
    })
  }

  const handleToggleReportsIsAnonymous = (value) => {
    scamReportServices.toggle_reports_is_anonymous({
      is_anonymous: value
    }).then(() => {
      setReportsIsAnonymous(value);
    }).catch((error) => {
      global.pushError(error);
    })
  }

  const handleUpdateScamSettings = (item, value) => {
    const payload = settingOptions.map((o) => ({
      category_id: o.category.id,
      enabled: item.category.id === o.category.id ? value : o.enabled
    }))
    scamSettingServices.update_scam_settings(payload).then(() => {
      fetchScamSettings();
    }).catch((error) => {
      global.pushError(error);
    })
  }

  const handleAdd = () => {
    const isValid = global.patterns.WHITELIST_URL.test(url);
    if (isValid) {
      setCallingAPIUrl(true)
      scamSettingServices.create_whitelist_url(url).then(() => {
        fetchWhitelistUrls();
        setWhitelistUrls([...whitelistUrls, url])
        setUrl('')
      }).catch((error) => {
        global.pushError(error);
      }).finally(() => {
        setCallingAPIUrl(false)
      })
    } else {
      global.pushError({
        message: t('notification.error.check_scam.url_invalid')
      })
    }
  }

  const removeWhitelistUrl = (index) => {
    scamSettingServices.delete_whitelist_url(whitelistUrls[index].id).then(() => {
      fetchWhitelistUrls();
    }).catch((error) => {
      global.pushError(error);
    })
  }

  return (
    <div className="scam-setting layout-content flex flex-col gap-4">
      <PageHeader
        title={t('scam_setting.title')}
        subtitle={t('scam_setting.description')}
        actions={[]}
      />
      <div className="flex flex-col gap-6">
        <Card
          title={
            <div className="flex items-center gap-2">
              <SmileOutlined className="text-black-500"/>
              <p>{t('scam_setting.incognito_mode.title')}</p>
            </div>
          }
        >
          <List
            itemLayout="horizontal"
            dataSource={incognitoModeOptions}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Switch
                    checked={item.enabled}
                    onChange={(v) => handleToggleReportsIsAnonymous(v)}
                  />
                ]}
              >
                <p>
                  {t(`scam_setting.incognito_mode.${item.label}`)}
                </p>
              </List.Item>
            )}
          />
        </Card>
        <Card
          title={
            <div className="flex items-center gap-2">
              <SettingOutlined className="text-black-500"/>
              <p>{t('scam_setting.setting.title')}</p>
            </div>
          }
        >
          <List
            itemLayout="horizontal"
            dataSource={settingOptions}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Switch
                    checked={item.enabled}
                    onChange={(v) => handleUpdateScamSettings(item, v)}
                  />
                ]}
              >
                <p>
                  {t(`scam_setting.setting.${item.category.id}`)}
                </p>
              </List.Item>
            )}
          />
        </Card>
        <Card
          title={
            <div className="flex items-center gap-2">
              <SettingOutlined className="text-black-500"/>
              <p>{t('scam_setting.whitelist_urls.title')}</p>
            </div>
          }
        >
          <div className="flex flex-col gap-5">
            <p className="text-black-500">
              {t('scam_setting.whitelist_urls.description')}
            </p>
            <Row gutter={[16, 16]}>
              <Col xl={8} lg={12} span={24}>
                <div className="flex items-center gap-3 w-full"> 
                  <Input
                    value={url}
                    disabled={callingAPIUrl}
                    placeholder={t('placeholder.url')}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                  <Button
                    type="primary"
                    loading={callingAPIUrl}
                    onClick={handleAdd}
                  >
                    {t('button.add')}
                  </Button>
                </div>
              </Col>
            </Row>
            {
              whitelistUrls.length > 0 && <Row gutter={[12, 12]}>
                {
                  whitelistUrls.map((wUrl, index) => <Col xl={8} lg={12} md={12} span={24} key={index}>
                    <div className="flex items-center justify-between border border-solid border-gray-200 rounded-md px-4 py-2">
                      <span>{wUrl.url}</span>
                      <DeleteOutlined
                        className="text-black-500 cursor-pointer"
                        onClick={() =>  removeWhitelistUrl(index)}
                      />
                    </div>
                  </Col>)
                }
              </Row>
            }
          </div>
        </Card>
      </div>
    </div>
  );
}

export default ScamSetting;